import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { HotelPlannerBlock } from '@voll-intelligence/genui-contract';
import type { HotelStage } from './hotel_selection.js';

/** Travamento da NAVEGAÇÃO (abrir a gaveta), distinto do travamento de escrita.
 *
 *  Na bancada determinística (`fullscreen`), `readOnly` quer dizer "esta
 *  superfície não escreve na CONVERSA" — a escolha acontece depois, na gaveta e no
 *  checkout do host. Navegar não é escrever, então as linhas da lista (que só
 *  ABREM a gaveta) não entram nesse travamento; seleção, busca e cabeçalho
 *  continuam travados por `locked`. É o mesmo tratamento que os cards do mapa já
 *  recebem sob `selectionLocked`, com que essas linhas foram alinhadas. No caminho
 *  conversacional nada muda: devolve o próprio `locked`.
 *
 *  `ui.viewOnly` NÃO entra aqui: ele congela os CRITÉRIOS de um bloco resolvido
 *  (ver `searchLocked`), e é exatamente nesse estado que a leitura do hotel e a
 *  escolha do quarto acontecem. Barrar a gaveta por ele deixava a reserva
 *  inalcançável na bancada — a mesma armadilha que o caminho conversacional já
 *  evita. */
function browseLock({
  fullscreen,
  busy,
  locked,
}: {
  readonly fullscreen: boolean;
  readonly busy: boolean;
  readonly locked: boolean;
}): boolean {
  if (!fullscreen) return locked;
  return busy;
}

/** O prazo das ofertas deste bloco já passou?
 *
 *  Wall-clock lido no RENDER, sem timer: o card não precisa congelar no segundo
 *  exato do vencimento — precisa estar congelado quando o viajante volta a ele,
 *  que é o caso real (a conversa reaberta no dia seguinte). Um timer só para
 *  cobrir a aba deixada aberta por 24 h pagaria re-render em todo card de hotel
 *  do histórico.
 *
 *  Prazo ausente ou malformado NUNCA congela: bloco antigo não tem o campo, e
 *  uma data que não parseia não é evidência de vencimento (ver `expiresAt` em
 *  `hotel_planner_schema.ts`). */
function searchExpired(expiresAt: string | undefined): boolean {
  if (!expiresAt) return false;
  const deadline = Date.parse(expiresAt);
  return Number.isFinite(deadline) && deadline <= Date.now();
}

/** Os cadeados do card, que NÃO são um só:
 *
 *  - `locked` é a ESCOLHA (trocar de hotel), e só morre com a conversa encerrada
 *    ou um planner-ou-checkout mais novo em cena — o host manda isso em `readOnly`.
 *  - `searchLocked` é a BUSCA: `ui.viewOnly` congela os critérios de um bloco já
 *    resolvido sem tirar a lista de hotéis do ar; `ui.expiresAt` faz o mesmo por
 *    TEMPO, quando as ofertas do bloco já não valem (ver `searchExpired`).
 *  - `detailsLocked` é a NAVEGAÇÃO (ver `browseLock`).
 *  - `turnSearch` diz se refinar vira TURNO em vez de leitura in-place. Sem canal
 *    de conversa (bancada determinística) não existe turno: lá a busca é sempre a
 *    leitura local que o host cumpre. */
export function resultsLocks({
  ui,
  stage,
  readOnly,
  busy,
  superseded,
  dispatch,
  fullscreen,
}: {
  readonly ui: HotelPlannerBlock;
  readonly stage: HotelStage;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly superseded: boolean;
  readonly dispatch: GenUiDispatch | undefined;
  readonly fullscreen: boolean;
}) {
  const locked = readOnly || busy;
  // Mesma leitura de `browseLock` para as TRÊS leituras da bancada: na bancada,
  // `readOnly` é "não escreve na CONVERSA", e navegar, refinar e buscar ali não
  // escrevem — são leitura local que o host cumpre (ver `turnSearch`). Herdar
  // `locked` deixava a barra inerte no topo da tela e a coluna de cortes e o
  // ordenador `disabled`, prometendo uma edição que nunca acontecia. `busy`
  // continua travando os três.
  const readingLocked = browseLock({ fullscreen, busy, locked });
  return {
    locked,
    searchLocked: readingLocked || ui.viewOnly === true || searchExpired(ui.expiresAt),
    /** O painel da lista completa: cortes, ordem e os cards do mapa. */
    panelLocked: readingLocked,
    detailsLocked: readingLocked,
    searchIsTurn: superseded && dispatch !== undefined,
    /** O preenchimento guiado da barra (`HotelSearchBarProps.guided`): só no
     *  PASSO DE BUSCA do card que ainda é o último da conversa. Com resultado
     *  em cena nada está pendente e refinar é gesto avulso; superado, o guia
     *  abriria popover no meio da thread com a conversa já seguindo abaixo. */
    searchGuided: stage === 'search' && !superseded,
  };
}
